import { useMemo, useState } from "react";
import { IoClose } from "react-icons/io5";
import Cookies from "js-cookie";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import { Button } from "@/components/ui/button/button";
import { cn } from "@/lib/utils";
import { CreateAttendanceRequest } from "./comps/create-att-request";
import { MarkYourselfAbsent } from "./comps/mark-absent";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/use-auth";
import { getAttendanceCommentsByDateAsAdmin } from "@/config/queries/admin/admin-queries";
import { format } from "date-fns";
import { BiComment } from "react-icons/bi";

export type Attendance = {
  status: string;
  date: string | Date;
  id: string;
  checkinTime: string;
  checkoutTime: string;
};

interface Props {
  attendance: Attendance;
}

const ATTENDANCE_COLORS: {
  [key: string]: string;
} = {
  "Half-day": "#FFFF00",
  Present: "#00FF00",
  Absent: "#FF0000",
  "Paid Leave": "#00CED1",
  "On Leave": "#FFA500",
  "Not Counting": "#808080",
};

const ATTENDANCE_FOREGROUNDS: {
  [key: string]: string;
} = {
  "Half-day": "black",
  Present: "black",
  Absent: "black",
  "Paid Leave": "black",
  "On Leave": "black",
  "Not Counting": "black",
};

export const AttendanceEvent = ({ attendance }: Props) => {
  const key = Cookies.get("key") as string;
  const [open, setOpen] = useState(false);

  const [step, setStep] = useState<"request" | "mark-absent" | "">("");

  const handleClose = () => {
    setStep("");
  };

  const { data: UserData } = useAuth();

  const colorAtt = ATTENDANCE_FOREGROUNDS[attendance.status];
  const bgAtt = ATTENDANCE_COLORS[attendance.status];

  const commentDate = useMemo(() => {
    if (attendance?.date) {
      let date = new Date(attendance?.date);
      date.setHours(0, 0, 0, 0);
      date = new Date(date);

      // date = new Date(date.getTime() - 5.5 * 60 * 60 * 1000);

      return date;
    }

    let date = new Date();
    date.setHours(0, 0, 0, 0);
    date = new Date(date);

    // date = new Date(date.getTime() - 5.5 * 60 * 60 * 1000);

    return date;
  }, [attendance]);

  const [isCommentOpen, setIsCommentOpen] = useState(false);

  const { data: CommentsData, isPending: isCommentsLoading } = useQuery({
    queryKey: [
      "attendance-comments",
      {
        employee: UserData?.user?._id as string,
        date: commentDate,
      },
    ],
    queryFn: () =>
      getAttendanceCommentsByDateAsAdmin(key)({
        employee: UserData?.user?._id as string,
        date: commentDate,
      }),
    // enabled: isCommentOpen,
  });

  const commentsByDate = useMemo(() => {
    if (isCommentsLoading) {
      return [];
    }

    if (CommentsData === undefined || CommentsData?.comments === undefined) {
      return [];
    }

    const result = CommentsData?.comments?.map((com) => {
      return {
        id: com?._id,
        adminComment: com?.adminComment?.[0],
        date: com?.date,
      };
    });

    return result;
  }, [CommentsData, isCommentsLoading]);

  return (
    <div>
      <div
        style={{
          backgroundColor: bgAtt,
          color: colorAtt,
        }}
      >
        <div className={cn("flex items-center justify-between pr-2", colorAtt)}>
          <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger className="tracking-normal">
              <div
                className={cn(
                  "flex items-center justify-between px-3 py-1",
                  colorAtt
                )}
              >
                <div>{attendance?.status}</div>
              </div>
            </AlertDialogTrigger>
            <AlertDialogContent
              className={cn("transition-all duration-300", step && "max-w-5xl")}
            >
              <AlertDialogCancel className="absolute right-2 top-2 h-7 w-7 p-0 border-none">
                <IoClose className="w-5 h-5" />
              </AlertDialogCancel>
              <AlertDialogHeader>
                <AlertDialogTitle>Attendance</AlertDialogTitle>
              </AlertDialogHeader>

              <div className="flex">
                <div className="flex flex-col p-3 shrink-0">
                  <h5>
                    <span>Attendance Marking : </span>
                    <span className="font-semibold">{attendance?.status}</span>
                  </h5>
                  <h5 className="mt-1">
                    <span>Checkin Time : </span>
                    <span className="font-semibold">
                      {attendance?.checkinTime}
                    </span>
                  </h5>
                  <h5 className="mt-1">
                    <span>Checkout Time : </span>
                    <span className="font-semibold">
                      {attendance?.checkoutTime}
                    </span>
                  </h5>

                  <div className="flex mt-1 flex-col gap-2">
                    <p className="text-accent-foreground text-[13px] mt-5">
                      If you want to make an attendance request, then
                    </p>

                    <Button
                      size="sm"
                      onClick={() => {
                        if (step === "" || step !== "request") {
                          setStep("request");
                        } else {
                          setStep("");
                        }
                      }}
                    >
                      Request Attendance
                    </Button>
                  </div>

                  {attendance?.status === "Not Counting" && (
                    <div className="flex mt-1 flex-col gap-2">
                      <p className="text-accent-foreground text-[13px] mt-5">
                        If you want to mark yourself absent, then
                      </p>

                      <Button
                        size="sm"
                        onClick={() => {
                          if (step === "" || step !== "mark-absent") {
                            setStep("mark-absent");
                          } else {
                            setStep("");
                          }
                        }}
                      >
                        Mark yourself absent
                      </Button>
                    </div>
                  )}
                </div>
                {step === "request" && (
                  <div className="w-full border-l px-5 py-2 flex flex-col gap-3">
                    <h2 className="text-lg font-medium">
                      Make an attendance request
                    </h2>

                    <CreateAttendanceRequest
                      date={attendance?.date}
                      attId={attendance?.id}
                      handleClose={handleClose}
                    />
                  </div>
                )}

                {step === "mark-absent" && (
                  <div className="w-full border-l px-5 py-2 flex flex-col gap-3">
                    <h2 className="text-lg font-medium">
                      Mark yourself absent
                    </h2>

                    <MarkYourselfAbsent
                      attId={attendance?.id}
                      handleClose={handleClose}
                    />
                  </div>
                )}
              </div>
            </AlertDialogContent>
          </AlertDialog>

          <div>
            <AlertDialog open={isCommentOpen} onOpenChange={setIsCommentOpen}>
              <AlertDialogTrigger className="tracking-normal">
                <div className="flex items-center">
                  <span>{commentsByDate?.length}</span>
                  <BiComment className="w-3 h-3" />
                </div>
              </AlertDialogTrigger>
              <AlertDialogContent
                className={cn("transition-all duration-300 max-w-6xl")}
              >
                <AlertDialogCancel className="absolute right-2 top-2 h-7 w-7 p-0 border-none">
                  <IoClose className="w-5 h-5" />
                </AlertDialogCancel>
                <AlertDialogHeader>
                  <AlertDialogTitle>Attendance Comments</AlertDialogTitle>
                </AlertDialogHeader>

                <div className="flex flex-col gap-2">
                  {commentsByDate?.length > 0 ? (
                    <div className="mt-4 max-h-[500px] overflow-y-auto">
                      <div className="flex flex-col gap-1">
                        {commentsByDate?.map((comment, index) => {
                          return (
                            <div
                              className="flex items-center justify-between bg-gray-50 px-5 py-2 rounded-lg border"
                              key={index}
                            >
                              <p>{comment?.adminComment}</p>
                              <p className="text-[13px]">
                                {format(new Date(comment?.date), "PPP")}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 flex items-center justify-center">
                      <p>No comments yet.</p>
                    </div>
                  )}
                </div>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
      <div className="bg-white flex flex-col text-black px-1 max-w-full flex-wrap">
        <p className="text-[11px]">IN - {attendance?.checkinTime}</p>
        <p className="text-[11px]">OUT - {attendance?.checkoutTime}</p>
        {/* <p className="text-[11px]">Hours - {attendance?.hoursWorked}</p> */}
      </div>
    </div>
  );
};
